/**
 * Global ingestion job tracking.
 * Persists active job IDs in localStorage so they survive navigation.
 * Polls for status and fires completion callbacks.
 */

import { createContext, useContext, useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

interface IngestionState {
  /** Add job IDs to track */
  trackJobs: (ids: string[]) => void;
  /** Overall progress 0-100 across all active jobs */
  progress: number;
  /** True if any jobs are still processing */
  isProcessing: boolean;
  /** Recently completed notifications (auto-clear after display) */
  completedNotification: { approved: number; total: number } | null;
  clearNotification: () => void;
}

const IngestionContext = createContext<IngestionState>({
  trackJobs: () => {},
  progress: 0,
  isProcessing: false,
  completedNotification: null,
  clearNotification: () => {},
});

export const useIngestion = () => useContext(IngestionContext);

const STORAGE_KEY = 'squishy_active_jobs';
const SESSION_TOKEN_KEY = 'squishy_session_token';

function loadJobIds(): string[] {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  } catch { return []; }
}

function saveJobIds(ids: string[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
}

interface JobStatus {
  id: string;
  status: string;
  progress: number;
  questionsGenerated: number;
  questionsApproved: number;
  error: string | null;
}

export function IngestionProvider({ children }: { children: React.ReactNode }) {
  const [activeIds, setActiveIds] = useState<string[]>(loadJobIds);
  const [jobStatuses, setJobStatuses] = useState<Map<string, JobStatus>>(new Map());
  const [notification, setNotification] = useState<{ approved: number; total: number } | null>(null);
  const qc = useQueryClient();
  const prevActiveRef = useRef<Set<string>>(new Set());

  const trackJobs = useCallback((ids: string[]) => {
    setActiveIds(prev => {
      const next = [...new Set([...prev, ...ids])];
      saveJobIds(next);
      return next;
    });
  }, []);

  // Poll active jobs
  useEffect(() => {
    if (activeIds.length === 0) return;

    const poll = async () => {
      const token = await getToken();
      if (!token) return;

      const results: JobStatus[] = [];
      for (const id of activeIds) {
        try {
          const res = await fetch(`${getApiUrl()}/ingest/jobs/${id}`, {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
              ...(getSessionToken() ? { 'x-session-token': getSessionToken()! } : {}),
            },
          });
          if (res.ok) {
            const data = await res.json();
            results.push({
              id,
              status: data.job.status,
              progress: data.job.progress,
              questionsGenerated: data.job.questionsGenerated ?? data.job.questions_generated ?? 0,
              questionsApproved: data.job.questionsApproved ?? data.job.questions_approved ?? 0,
              error: data.job.error,
            });
          } else if (res.status === 401 || res.status === 404) {
            // Do not leave old jobs spinning forever if the session changed,
            // the job belongs to another user, or production rejected the poll.
            results.push({
              id,
              status: 'failed',
              progress: 100,
              questionsGenerated: 0,
              questionsApproved: 0,
              error: res.status === 401 ? 'unauthorized' : 'not_found',
            });
          }
        } catch {}
      }

      setJobStatuses(new Map(results.map(r => [r.id, r])));

      // Check for newly completed jobs
      const stillActive: string[] = [];
      let newlyApproved = 0;
      let newlyGenerated = 0;
      let anyNewlyCompleted = false;

      for (const r of results) {
        if (r.status === 'complete' || r.status === 'failed') {
          if (prevActiveRef.current.has(r.id)) {
            anyNewlyCompleted = true;
            newlyApproved += r.questionsApproved;
            newlyGenerated += r.questionsGenerated;
          }
        } else {
          stillActive.push(r.id);
        }
      }

      // Also keep IDs we haven't fetched yet
      for (const id of activeIds) {
        if (!results.find(r => r.id === id)) stillActive.push(id);
      }

      prevActiveRef.current = new Set(activeIds);

      if (anyNewlyCompleted && stillActive.length === 0) {
        // All jobs done — show notification
        setNotification({ approved: newlyApproved, total: newlyGenerated });
        // Invalidate feed queries so new questions appear
        qc.invalidateQueries({ queryKey: ['feed'] });
        qc.invalidateQueries({ queryKey: ['me'] });
      }

      if (stillActive.length < activeIds.length) {
        setActiveIds(stillActive);
        saveJobIds(stillActive);
      }
    };

    poll();
    const interval = setInterval(poll, 3000);
    return () => clearInterval(interval);
  }, [activeIds, qc]);

  // Compute overall progress
  const statuses = Array.from(jobStatuses.values());
  const activeStatuses = statuses.filter(s => s.status !== 'complete' && s.status !== 'failed');
  const progress = activeIds.length > 0 && activeStatuses.length > 0
    ? Math.round(activeStatuses.reduce((sum, s) => sum + s.progress, 0) / activeStatuses.length)
    : 0;

  return (
    <IngestionContext.Provider value={{
      trackJobs,
      progress,
      isProcessing: activeIds.length > 0,
      completedNotification: notification,
      clearNotification: () => setNotification(null),
    }}>
      {children}
    </IngestionContext.Provider>
  );
}

// Helpers to get auth token and API URL without importing circular deps
async function getToken(): Promise<string | null> {
  try {
    const { fetchAuthSession } = await import('aws-amplify/auth');
    const session = await fetchAuthSession();
    return session.tokens?.accessToken?.toString() ?? null;
  } catch { return null; }
}

function getApiUrl(): string {
  return import.meta.env.VITE_API_URL ?? '/api';
}

function getSessionToken(): string | null {
  try {
    return localStorage.getItem(SESSION_TOKEN_KEY);
  } catch {
    return null;
  }
}
